import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { useAppT, localizeStage, localizeResp } from "../components/home/i18n";
import { Search, Clock, History } from "lucide-react";

const STAGES = [
  "Applied", "Employer Screening", "Interview", "Offer/Contract", "Documents",
  "Training", "Recognition Assessment", "ZAV Approval", "Visa",
  "Pre-departure", "Arrival & Integration", "Completed", "Declined", "Withdrawn",
];
const CONDITIONAL = new Set(["Recognition Assessment", "ZAV Approval", "Visa"]);
const RESP = ["employer", "candidate", "authority", "admin"];
const RESP_TONE = { candidate: "text-[#2E6B4B]", employer: "text-[#1F4A38]", admin: "text-[#8B3A3A]", authority: "text-[#b0722a]" };

function Manage({ app, onDone, a }) {
  const [stage, setStage] = useState(app.current_stage);
  const [responsible, setResponsible] = useState(app.responsible || "admin");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await API.post(`/admin/applications/${app.id}/stage`, { stage, responsible, note, due_date: due || null });
      toast.success(`${a.adminApps.stageSetTo} ${localizeStage(stage, a)}`);
      setNote(""); setDue("");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 grid gap-3 rounded-sm border border-[#E8E4DB] bg-[#FDFBF7] p-3 md:grid-cols-2">
      <label className="text-xs text-[#5C5855]">{a.adminApps.stage}
        <select data-testid={`stage-select-${app.id}`} value={stage} onChange={(e) => setStage(e.target.value)} className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-2 py-2 text-sm">
          {STAGES.map((s) => <option key={s} value={s}>{localizeStage(s, a)}{CONDITIONAL.has(s) ? ` ${a.adminApps.conditional}` : ""}</option>)}
        </select>
      </label>
      <label className="text-xs text-[#5C5855]">{a.adminApps.responsibleParty}
        <select data-testid={`resp-select-${app.id}`} value={responsible} onChange={(e) => setResponsible(e.target.value)} className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-2 py-2 text-sm">
          {RESP.map((r) => <option key={r} value={r}>{localizeResp(r, a)}</option>)}
        </select>
      </label>
      <label className="text-xs text-[#5C5855]">{a.adminApps.nextActionNote}
        <input data-testid={`note-input-${app.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={a.adminApps.nextPlaceholder} className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-2 py-2 text-sm" />
      </label>
      <label className="text-xs text-[#5C5855]">{a.adminApps.dueDate}
        <input data-testid={`due-input-${app.id}`} type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-2 py-2 text-sm" />
      </label>
      <div className="md:col-span-2">
        <button data-testid={`save-stage-${app.id}`} disabled={busy} onClick={save} className="rounded-full bg-[#1F4A38] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? a.common.saving : a.adminApps.updateStage}</button>
        <span className="ml-3 text-[11px] text-[#8a8580]">{a.adminApps.conditionalNote}</span>
      </div>
    </div>
  );
}

export default function AdminApplications() {
  const { a } = useAppT();
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (stage) params.stage = stage;
    const { data } = await API.get("/admin/applications", { params });
    setApps(data.applications);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);

  return (
    <AdminLayout>
      <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminApps.title}</h1>
      <p className="mt-1 text-sm text-[#5C5855]">{a.adminApps.sub}</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-[#E8E4DB] bg-white px-3 py-1.5">
          <Search className="h-4 w-4 text-[#8a8580]" />
          <input data-testid="app-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={a.adminApps.searchPlaceholder} className="w-full text-sm outline-none" />
        </div>
        <select data-testid="app-stage-filter" value={stage} onChange={(e) => setStage(e.target.value)} className="rounded-full border border-[#E8E4DB] bg-white px-3 py-1.5 text-sm">
          <option value="">{a.adminApps.allStages}</option>
          {STAGES.map((s) => <option key={s} value={s}>{localizeStage(s, a)}</option>)}
        </select>
        <button data-testid="app-search-btn" onClick={load} className="rounded-full bg-[#1F4A38] px-4 py-1.5 text-sm font-semibold text-white">{a.adminApps.search}</button>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-applications-list">
        {apps.length === 0 && <div className="text-sm text-[#5C5855]">{a.adminApps.noMatch}</div>}
        {apps.map((ap) => (
          <div key={ap.id} data-testid={`admin-app-${ap.id}`} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-serif text-lg text-[#173A2C]">{ap.job_title} <span className="text-sm text-[#5C5855]">· {ap.employer_org}</span></div>
                <div className="text-xs text-[#5C5855]">{a.adminApps.candidateWord} {ap.alias} · {a.adminApps.statusWord} {ap.status}</div>
              </div>
              <div className="text-right">
                <span className="rounded-full bg-[#1F4A38] px-3 py-0.5 text-[11px] font-semibold text-white">{localizeStage(ap.current_stage, a)}</span>
                <div className={`mt-1 text-[11px] ${RESP_TONE[ap.responsible] || "text-[#5C5855]"}`}>{a.adminApps.responsibleWord} {localizeResp(ap.responsible, a)}</div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2 rounded-sm bg-[#FDFBF7] p-2 text-xs text-[#5C5855]">
              <Clock className="mt-0.5 h-3.5 w-3.5" /> {a.adminApps.next} {ap.next_action}{ap.due_date ? ` · ${a.common.due} ${ap.due_date}` : ""} · {a.common.updated} {ap.last_update ? new Date(ap.last_update).toLocaleDateString() : "—"}
            </div>
            <button data-testid={`manage-app-${ap.id}`} onClick={() => setOpenId(openId === ap.id ? null : ap.id)} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#1F4A38]"><History className="h-4 w-4" /> {openId === ap.id ? a.adminApps.hide : a.adminApps.manageHistory}</button>
            {openId === ap.id && (
              <div>
                <Manage app={ap} onDone={load} a={a} />
                <ol className="mt-3 space-y-1 border-l-2 border-[#E8E4DB] pl-4" data-testid={`history-${ap.id}`}>
                  {ap.stages.map((s, i) => (
                    <li key={i} className="relative">
                      <span className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ${s.stage === ap.current_stage ? "bg-[#C4A46B]" : "bg-[#2E6B4B]"}`} />
                      <div className="text-sm text-[#2C2A29]">{localizeStage(s.stage, a)} <span className={`text-[11px] ${RESP_TONE[s.responsible] || ""}`}>({localizeResp(s.responsible, a)}, {a.adminApps.byWord} {localizeResp(s.actor_role, a)})</span></div>
                      {s.note && <div className="text-xs text-[#5C5855]">{s.note}</div>}
                      <div className="text-[10px] text-[#8a8580]">{new Date(s.created_at).toLocaleString()}{s.due_date ? ` · ${a.common.due} ${s.due_date}` : ""}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
